import styled from '@emotion/styled';
import { AxisLeft, TickLabelProps } from '@visx/axis';
import { LinearGradient } from '@visx/gradient';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import type { ColorScalePoint } from '../../../utils/metric-utils';
import { riskScoreColorScaleConfig } from '../../../utils/metric-utils';
import { positioning } from '../../plots/trendchart/chart/chart-styles';
import { theme } from '../../shared/theme';

const scaleTicks = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

export function RiskScoreIllustration() {
  const indexHeight = 500;
  const graphHeight = indexHeight - 20;
  const svgPadding = { left: 40, right: 20 }; // Padding inside the SVG
  const contentWidth = 510; // Increased width for axis, bar, and text
  const svgWidth = contentWidth + svgPadding.left + svgPadding.right; // Total SVG width
  const descriptionX = 38; // Start descriptions slightly further from the bar
  const descriptionWidth = contentWidth - descriptionX; // Calculate description width

  // Create a linear scale for the Y axis (0-100)
  const yScale = scaleLinear({
    domain: [0, 100],
    range: [graphHeight, 0], // Invert range for SVG coordinates (0 is top)
  });

  const fontSize = 12;
  // Function to render tick labels
  const renderTickLabel = (props: TickLabelProps<number> & { formattedValue?: string }) => {
    // eslint-disable-next-line react/prop-types
    const { formattedValue } = props;
    if (!formattedValue) return null;
    return (
      <text {...props} dy=".33em" fontSize={fontSize}>
        {formattedValue}
      </text>
    );
  };

  const offset = 10;

  return (
    <RiskScaleContainer>
      {/* Set SVG width explicitly and remove internal group offset */}
      <svg width={svgWidth} height={indexHeight} style={{ overflow: 'visible' }}>
        <Group left={svgPadding.left} top={positioning.margin.top / 2}>
          {/* Left Axis for scale numbers - Position relative to Group */}
          <AxisLeft
            scale={yScale}
            left={-10} // Position axis slightly left of the bar (relative to Group)
            hideAxisLine={true}
            tickStroke={'white'}
            numTicks={scaleTicks.length}
            tickValues={scaleTicks}
            axisClassName="risk-level-info-axis-label"
            tickComponent={renderTickLabel}
          />

          {/* The colored risk score bar */}
          <defs>
            <LinearGradient id="risk-score-info-gradient" x1="0%" y1="100%" x2="0%" y2="0%">
              {riskScoreColorScaleConfig.map((point: ColorScalePoint) => (
                <stop key={point.value} offset={`${point.value * 100}%`} stopColor={point.color} />
              ))}
            </LinearGradient>
          </defs>
          <rect
            x={0} // Position bar at x=0 within the Group
            y={0} // Position bar at y=0 within the Group
            width={40} // Bar width
            height={graphHeight} // Bar height matches graph area
            fill="url(#risk-score-info-gradient)"
            rx={theme.borders.radius.small}
          />

          {/* Level Descriptions using foreignObject - Position relative to Group */}
          <foreignObject x={descriptionX} y={yScale(100) - offset} width={descriptionWidth} height={20}>
            <LevelReference>— Most polluted raw wastewater (WW) in Resistomap database</LevelReference>
          </foreignObject>
          <foreignObject x={descriptionX} y={yScale(50) - offset} width={descriptionWidth} height={20}>
            <LevelReference>— Worst, raw WW diluted with an equal amount of spring water</LevelReference>
          </foreignObject>
          <foreignObject x={descriptionX} y={yScale(20) - offset} width={descriptionWidth} height={20}>
            <LevelReference>— Two parts worst, raw WW and eight parts spring water</LevelReference>
          </foreignObject>
          <foreignObject x={descriptionX} y={yScale(10) - offset} width={descriptionWidth} height={20}>
            <LevelReference>— Worst European surface water ≈ 2 dl of WW in a 10L water bucket</LevelReference>
          </foreignObject>
          <foreignObject x={descriptionX} y={yScale(5) - offset} width={descriptionWidth} height={20}>
            <LevelReference>— Add 1 dl of worst, raw WW into a 10 L bucket of spring water</LevelReference>
          </foreignObject>
          <foreignObject x={descriptionX} y={yScale(0) - offset} width={descriptionWidth} height={20}>
            <LevelReference>— Pure spring water</LevelReference>
          </foreignObject>
        </Group>
      </svg>
    </RiskScaleContainer>
  );
}

const RiskScaleContainer = styled.div`
  padding-top: 10px;
  width: 100%; // Take full width of the modal body section
  display: flex;
  position: relative;
`;

// Style for the axis labels (can be customized)
// Note: Ensure this style is applied globally or imported correctly if needed.
// Example using global style tag (consider alternatives for component isolation):
// <style dangerouslySetInnerHTML={{ __html: `
//   .risk-level-info-axis-label tspan {
//     font-size: 12px;
//     color: ${theme.colors.neutral700};
//     text-anchor: end;
//   }
// `}} />

const LevelReference = styled.div`
  font-size: ${theme.fontSize.medium};
  color: ${theme.colors.neutral800};
  padding-left: 10px; // Space from the color bar
`;
